import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, FormProvider, useWatch, Controller } from "react-hook-form";
import Checkbox from "../form-fields/checkbox";
import Card from "../card";
import InputField from "../form-fields/InputField";
import Radio from "../form-fields/radio";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import SelectField from "../form-fields/SelectField";
import './tenant.css';
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import MultipleFileUploader from "../CustomUploader/MultipleFileUploader";
import { UserLoginData } from "../../components/common/charts/Enums";
import { DocumentEntityTypesEnum, DocumentService, DocumentTypesEnum, LeaseAgreementService, LeaseApplicationService, TemplateService, TemplateTypeEnum, TenantLeaseTypesEnum, TenantService, UnitService } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { URLS } from "../../constants/constants";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import RadioGroup from "../form-fields/RadioGroup";
import HorizontalMultipleFileUploader from "../CustomUploader/HorizontalMultipleFileUploader";
import PhoneInput from "../form-fields/PhoneInput";
import LeaseOverlapModal from "./LeaseOverlapModal";
import EmailInput from "../form-fields/EmailInput";
import CurrencyInput from "../form-fields/CurrencyInput";
import { handleApiErrors } from "../../utils/handleApiErrors";
import { getWhatsAppEnabled } from "../../services/whatsApp.service";

type FormValues = {
  tenantType: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  whatsAppOptIn: boolean;
  unitId: string;
  templateId: string;
  leaseType?: string;
  securityDeposit?: string;
  rent?: string;
  startDate?: string;
  endDate?: string;
  leaseApplication?: File[];
  leaseAgreement?: File[];
};

interface AddTenantProps {
  fetchData?: any;
}

const AddTenant = ({ fetchData }: AddTenantProps) => {
  const [templateData, setTemplateData] = useState([]);

  const [unitData, setUnitData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [isLeaseOverlapModalOpen, setIsLeaseOverlapModalOpen] = useState(false);
  const [existingLeaseData, setExistingLeaseData] = useState<any>({});
  const [isLoadingLeaseData, setIsLoadingLeaseData] = useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] = useState<FormValues | null>(null);
  const [pendingTenantId, setPendingTenantId] = useState<number | null>(null);
  const [createdTenantId, setCreatedTenantId] = useState<number | null>(null);
  const [whatsAppEnabled, setWhatsAppEnabled] = useState(false);

  const organization_id = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const userId = Number(sessionStorage.getItem(UserLoginData.user_id));

  const methods = useForm<FormValues>({
    defaultValues: {
      tenantType: "1",
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      whatsAppOptIn: false,
      unitId: "",
      templateId: "",
      leaseType: TenantLeaseTypesEnum.FixedTermLease.toString()
    },
    mode: "onBlur"
  });


  const { handleSubmit, control, getValues, setError, formState: { isDirty, isValid } } = methods;
  const tenantType = useWatch({ control, name: "tenantType" });
  const leaseType = useWatch({ control, name: "leaseType" });

  const fetchInitialData = useCallback(async () => {
    try {
      const [templates, units, waEnabled] = await Promise.all([
        apiHelper(
          TemplateService.getApiTemplateOrganizationlist,
          { showNotification: false },
          organization_id,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          `templateTypeId == ${TemplateTypeEnum.LeaseApplication}`
        ),
        apiHelper(
          UnitService.getApiUnitOrganizationlookup,
          { showNotification: false },
          organization_id,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          "!Tenants.Any()"
        ),
        getWhatsAppEnabled(),
      ]);

      setTemplateData(templates?.data?.success || []);
      setUnitData(units?.data?.success || []);
      setWhatsAppEnabled(waEnabled);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    }
  }, [organization_id]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const templateOptions = useMemo(() => (
    templateData.map((t: any) => ({ Id: t.id, Name: t.name }))
  ), [templateData]);

  const unitOptions = useMemo(() => (
    unitData.map((t: any) => ({ Id: t.id, Name: t.name }))
  ), [unitData]);

  const handleFetchExistingLease = (id: string | number, isId: boolean = false) => {
    setIsLoadingLeaseData(true);
    const serviceCall = isId
        ? LeaseAgreementService.getApiLeaseAgreement1
        : LeaseAgreementService.getApiLeaseAgreementUnitlist;

    apiHelper(
      serviceCall,
      { showNotification: false },
      Number(id),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    ).then((res) => {
      const data = res?.data?.success;
      const lease = Array.isArray(data) ? data[0] : data;
      setExistingLeaseData(lease?.formatted || {});
      setIsLoadingLeaseData(false);
    }).catch(() => {
      setIsLoadingLeaseData(false);
    })
  }

  const handleOnboardNewTenant = useCallback(async (formData: FormValues) => {

    try {
      const tenantResponse = await apiHelper(
        TenantService.postApiTenant,
        {
          successMessage: "Tenant Invited Successfully",
          showNotification: true,
        },
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          whatsAppOptIn: formData.whatsAppOptIn,
          unitId: formData.unitId,
          userId: userId,
        }
      );

      if (tenantResponse?.data?.success) {

        const leaseResponse = await apiHelper(
          LeaseApplicationService.postApiLeaseApplication,
          {
            successMessage: "Tenant Invited Successfully",
            showNotification: true,
          },
          {
            tenantId: tenantResponse.data.success.id,
            unitId: formData.unitId,
            templateId: formData.templateId,
          }
        );

        if (leaseResponse?.data?.success) {

          methods.reset();
          fetchData?.();

          // Navigate to the referrer page or default
          const referrer = location.state?.from || URLS.TENANTS;
          navigate(referrer, { state: { activeTab: location.state?.activeTab } });
        }
      }

      if (tenantResponse?.errors) {
        handleApiErrors(tenantResponse.errors, setError);
        return;
      }

    } catch (error) {
      throw error;
    }
  }, [userId, navigate, fetchData]);


  const handleExistingTenant = useCallback(async (formData: FormValues, isOverlapped: boolean = false, passedTenantId?: number) => {

    try {
      let tenantId = passedTenantId || createdTenantId;

      if (!tenantId) {
        const tenantResponse = await apiHelper(
          TenantService.postApiTenant, {
          showNotification: false,
        }, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          whatsAppOptIn: formData.whatsAppOptIn,
          unitId: formData.unitId,
          userId: userId,
        }
        );


        if (tenantResponse?.data?.success) {
          tenantId = tenantResponse?.data?.success?.id;
          setCreatedTenantId(tenantId);
        }

        if (tenantResponse?.errors) {
          handleApiErrors(tenantResponse.errors, setError);
          return;
        }

      }

      if (tenantId) {
        const unitId = formData.unitId;

        let formattedEndDate = null;

        const formattedStartDate = new Date(formData.startDate).toISOString();
        if (formData.endDate) {
          formattedEndDate = new Date(formData.endDate).toISOString();
        }


        const leaseResponse = await apiHelper(
          LeaseAgreementService.postApiLeaseAgreement,
          {
            successMessage: "Tenant added successfully.",
            showNotification: false
          },
          {
            unitId: Number(unitId),
            tenantId: Number(tenantId),
            leaseTypeId: Number(formData.leaseType),
            SecurityDeposit: formData.securityDeposit,
            Rent: formData.rent,
            StartDate: formattedStartDate,
            EndDate: formattedEndDate,
            forceUpdate: isOverlapped
          }
        );

        if (leaseResponse.errors && Object.keys(leaseResponse.errors).length > 0) {
          const conflictEntityId = leaseResponse?.errors?.ConflictEntityId?.[0];
          if (conflictEntityId && conflictEntityId !== "0") {
            handleFetchExistingLease(conflictEntityId, true);
            setPendingSubmissionData(formData);
            setPendingTenantId(tenantId);
            setIsLeaseOverlapModalOpen(true);
            return;
          } else {
            const errors = leaseResponse?.errors || {};
            const otherErrorKey = Object.keys(errors).find(k => k !== "ConflictEntityId" && k !== "ConflictEntityTypeId" && k !== "leaseAgreementId");
            if (otherErrorKey) {
                PubSub.publish(TopicsEnum.Notification.toString(), {
                  message: errors[otherErrorKey][0],
                  type: NotificationType.Failure,
                });
            } 
            return;
          }
        }if (leaseResponse?.data?.success) {
          PubSub.publish(TopicsEnum.Notification.toString(), {
            message: "Tenant added successfully.",
            type: NotificationType.Success,
          });
          const documentPromises = [];
          const leaseAgreementId = leaseResponse.data.success.id;
          if (formData.leaseApplication?.[0]) {
            const leaseApplicationData = {
              EntityTypeId: DocumentEntityTypesEnum.Tenant,
              EntityId: tenantId,
              DocumentTypeId: DocumentTypesEnum.LeaseApplication,
              IsMain: false,
              FileName: formData.leaseApplication[0].name,
              Document: formData.leaseApplication[0],
            };
            documentPromises.push(
              apiHelper(
                DocumentService.postApiDocument,
                { showNotification: false },
                leaseApplicationData
              )
            );
          }

          if (formData.leaseAgreement?.[0]) {
            const leaseAgreementData = {
              EntityTypeId: DocumentEntityTypesEnum.Tenant,
              EntityId: tenantId,
              DocumentTypeId: DocumentTypesEnum.LeaseAgreement,
              IsMain: false,
              FileName: formData.leaseAgreement[0].name,
              Document: formData.leaseAgreement[0],
            };
            documentPromises.push(
              apiHelper(
                DocumentService.postApiDocument,
                { showNotification: false },
                leaseAgreementData
              )
            );
          }
          await Promise.all(documentPromises);
          methods.reset();
          fetchData?.();
          setCreatedTenantId(null);
          // Navigate to the referrer page or default
          const referrer = location.state?.from || URLS.TENANTS;
          navigate(referrer, { state: { activeTab: location.state?.activeTab } });
        }
      }

    } catch (error) {
      throw error;
    }
  }, [userId, navigate, fetchData, createdTenantId, setError, methods, location.state]);

  const onSubmit = useCallback(async (data: FormValues) => {
    setIsLoading(true);

    try {
      if (data.tenantType === "1") {
        await handleOnboardNewTenant(data);
      }
      else {

        await handleExistingTenant(data, false, createdTenantId || undefined);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [handleOnboardNewTenant, handleExistingTenant, createdTenantId]);

  const onCancel = useCallback(() => navigate(-1), [navigate]);

  const commonFields = (
    <>
      <div className="col-12 col-md-6 col-lg-4">
        <InputField
          name="firstName"
          label="First Name"
          required
          placeholder="Enter First Name"
          requiredMessage="First name is required"
          onlyAlphabets={true}
          validation={{
            minLength: { value: 2, message: "First name must be at least 2 characters long" },
            maxLength: { value: 20, message: "First name cannot exceed 20 characters" }
          }}
        />
      </div>

      <div className="col-12 col-md-6 col-lg-4">
        <InputField
          name="lastName"
          label="Last Name"
          required
          placeholder="Enter Last Name"
          requiredMessage="Last name is required"
          onlyAlphabets={true}
          validation={{
            minLength: { value: 2, message: "Last name must be at least 2 characters long" },
            maxLength: { value: 20, message: "Last name cannot exceed 20 characters" }
          }}
        />
      </div>

      <div className="col-12 col-md-6 col-lg-4">
        <EmailInput
          name="email"
          label="Email Address"
          required
          requiredMessage="Email is required"
          placeholder="Enter Email Address"
          validation={{
            maxLength: { value: 50, message: "Email cannot exceed 50 characters" }
          }}
        />
      </div>

      <div className="col-12 col-md-6 col-lg-4">
        <PhoneInput
          key={whatsAppEnabled ? "phone-wa-all" : "phone-us-only"}
          name="phoneNumber"
          label="Contact No"
          required
          requiredMessage="Phone number is required"
          placeholder="Enter Contact No"
          defaultCountry="us"
          onlyCountries={whatsAppEnabled ? [] : ["us"]}
        />
      </div>

      <div className="col-12 col-md-6 col-lg-4">
        <SelectField
          name="unitId"
          label="Unit"
          options={unitOptions}
          required
          placeholderText="Select Unit"
          requiredMessage="Unit is required"
          isSearchable={true}
        />
      </div>

      {/* Shown only when WhatsApp:Enabled is true on the API.
          Create also emails the tenant a WhatsApp join invite (backend TenantService). */}
      {whatsAppEnabled && (
        <div className="col-12">
          <Controller
            name="whatsAppOptIn"
            control={control}
            render={({ field }) => (
              <>
                <Checkbox
                  isChecked={field.value}
                  onChange={field.onChange}
                  label="Enable WhatsApp messages for this tenant"
                />
                {field.value ? (
                  <p className="text-muted small mb-0 mt-1">
                    The tenant will also receive an email with instructions to join WhatsApp
                    (number / QR code, and sandbox join code when configured).
                  </p>
                ) : null}
              </>
            )}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="d-flex flex-column gap-3 full-page-content create-tenant-container">
        <LeftArrowTopHeader 
          title="Tenants"
          backPath={URLS.TENANTS}
          onCancel={onCancel}
        />

        <FormProvider {...methods}>

          <Card>
            <div className="row">
              <div className="col-12">
                <div>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-12">
                        <RadioGroup
                          label="Tenant Type"
                          required
                          requiredMessage="Tenant type is required"
                        >
                          <Radio
                            name="tenantType"
                            value="1"
                            label="Onboard New Tenant"
                          />
                          <Radio
                            name="tenantType"
                            value="2"
                            label="Existing Tenant"
                          />
                        </RadioGroup>

                      </div>

                      {commonFields}

                      {tenantType === "1" && (
                        <div className="col-12 col-md-6 col-lg-4">
                          <SelectField
                            name="templateId"
                            label="Template"
                            options={templateOptions}
                            required
                            placeholderText="Select Template"
                            requiredMessage="Template is required"
                          />
                        </div>
                      )}

                      {tenantType === "2" && (
                        <>
                          <div className="col-12">
                            <RadioGroup
                              label="Lease Type"
                              required
                              requiredMessage="Lease type is required"
                            >
                              <Radio
                                name="leaseType"
                                value={TenantLeaseTypesEnum.FixedTermLease.toString()}
                                label="Fixed Lease"
                              />
                              <Radio
                                name="leaseType"
                                value={TenantLeaseTypesEnum.MonthToMonth.toString()}
                                label="Month To Month"
                              />
                            </RadioGroup>
                          </div>

                          <div className="col-12 col-md-6 col-lg-4">
                            <CurrencyInput
                              name="securityDeposit"
                              label="Security Deposit"
                              required
                            />
                          </div>

                          <div className="col-12 col-md-6 col-lg-4">
                            <CurrencyInput
                              name="rent"
                              label="Rent"
                              required
                            />
                          </div>

                          <div className="col-12 col-md-6 col-lg-4">
                            <InputField
                              name="startDate"
                              label="Start Date"
                              type="date"
                              required
                              placeholder="Enter Start Date"
                              validation={{
                                required: "Start Date is required"
                              }}
                            />
                          </div>

                          <div className="col-12 col-md-6 col-lg-4">
                            <InputField
                              name="endDate"
                              label={leaseType === TenantLeaseTypesEnum.MonthToMonth.toString() ? "End Date (Optional)" : "End Date"}
                              type="date"

                              required={leaseType === TenantLeaseTypesEnum.FixedTermLease.toString()}
                              placeholder="Enter End Date"
                              validation={{
                                // Validation rule: Required only if Fixed Term
                                required: leaseType === TenantLeaseTypesEnum.FixedTermLease.toString()
                                  ? "End Date is required"
                                  : false,
                                validate: (value) => {

                                  if (!value && leaseType === TenantLeaseTypesEnum.MonthToMonth.toString()) {
                                    return true;
                                  }

                                  const startDate = getValues("startDate");
                                  if (startDate && value && value <= startDate) {
                                    return "End Date must be after Start Date";
                                  }
                                  return true;
                                }
                              }}
                            />
                          </div>

                        </>
                      )}
                    </div>
                    {
                      tenantType === "2" && (
                        <div className="row">
                          <div className="col-12 col-md-6 col-lg-4">

                            <HorizontalMultipleFileUploader
                              name="leaseApplication"
                              label="Upload Lease Application"
                              allowedFormats={["application/pdf"]}
                              maxSizeMB={3}
                              maxFiles={1}
                              uploaderId="AddLeaseApplication"
                              required={true}
                              requiredMessage="Lease Application File is required"
                              uploadertext="Upload Document"
                              uploadertypeslabel="Max. size 3MB. .pdf"
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">

                            <HorizontalMultipleFileUploader
                              name="leaseAgreement"
                              uploaderId="AddLeaseAgreement"
                              label="Upload Lease Agreement"
                              allowedFormats={["application/pdf"]}
                              maxSizeMB={3}
                              maxFiles={1}
                              required={true}
                              requiredMessage="Lease Agreement File is required"
                              uploadertext="Upload Document"
                              uploadertypeslabel="Max. size 3MB. .pdf"
                            />
                          </div>
                        </div>

                      )
                    }
                  </form>
                </div>
              </div>
            </div>
          </Card>

          <FixedFooter
            onCancel={onCancel}
            onSubmit={handleSubmit(onSubmit)}
            loading={isLoading}
            buttonLabel={"Save"}
            isSubmitDisabled={isLoading || !isDirty || !isValid}
          />
        </FormProvider>
      </div>

      {/* For now add fake data */}

      <LeaseOverlapModal
        isOpen={isLeaseOverlapModalOpen}
        onClose={() => {
          setIsLeaseOverlapModalOpen(false);
          setPendingSubmissionData(null);
          setPendingTenantId(null);
        }}
        onContinue={() => {
          setIsLeaseOverlapModalOpen(false);
          if (pendingSubmissionData && pendingTenantId) {
            handleExistingTenant(pendingSubmissionData, true, pendingTenantId);
            setPendingSubmissionData(null);
            setPendingTenantId(null);
          }
        }}
        existingLease={
          {
            type: existingLeaseData?.LeaseTypeId,
            startDate: existingLeaseData?.StartDateDateOnly,
            endDate: existingLeaseData?.EndDateDateOnly,
            securityDeposit: existingLeaseData?.SecurityDeposit,
            securityDepositRaw: existingLeaseData?.SecurityDepositRaw,
            rent: existingLeaseData?.Rent,
            rentRaw: existingLeaseData?.RentRaw,
          }
        }
        newLeaseEndDate={DateHelper.getPreviousDay(getValues("startDate"))}
        isLoading={isLoadingLeaseData}
      />

    </>
  );
};

export default AddTenant;